import React, { useState, } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, } from 'reactstrap';
import { useToggle } from 'react-use';
import { toast } from 'react-toastify';
import { parse as parseCsv } from 'papaparse';
import { chunk, isEmpty, trim, } from 'lodash';

import firebase from '../../firebase';
import { batch, getCollectionData, getDocumentData, } from '../../shared/firebase';
import { serial, idWithTenant, } from '../../shared/util';
import { readCsvFile, productSkuErrors, } from '../../util';

const db = firebase.firestore();
const { values } = Object;

export default function ProductsBatchDeleteModal ({ isOpen, onClickClose, onFinish, tenant, productsGroupedBySku, componentProductIds, }) {
  const [fileName, setFileName] = useState('');
  const [items, setItems] = useState(null);
  const [isValidating, toggleValidating] = useToggle(false);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const validate = async (skus, i) => {
    const skuErrors = productSkuErrors(skus, i, productsGroupedBySku);
    if(!isEmpty(skuErrors)) return { i, sku: skus[i], errors: skuErrors };

    const [product] = productsGroupedBySku[skus[i]];
    const [{ goodInventoryIds = [], ngInventoryIds = [] }, [receivingRequest], [shippingRequest]] = await Promise.all([
      getDocumentData(product.ref),
      getCollectionData(tenant.ref.collection('receivingRequests').where('productIds', 'array-contains', product.id).limit(1)),
      getCollectionData(tenant.ref.collection('shippingRequests').where('productIds', 'array-contains', product.id).limit(1)),
    ]);
    return {
      i,
      sku: skus[i],
      product,
      errors: [
        componentProductIds.includes(product.id) && 'ケースまたはセット商品の構成品です',
        (goodInventoryIds.length + ngInventoryIds.length) > 0 && '在庫があります',
        (receivingRequest || shippingRequest) && '一度でも入出荷があった商品は削除できません',
      ].filter(_ => _),
    };
  };
  const onSelectFile = async ({ target, target: { files: [file] } }) => {
    if(!file) return;
    setItems(null);
    setFileName(file.name);
    toggleValidating(true);
    try {
      const { data: csvRows } = parseCsv(await readCsvFile(file), { header: true });
      const skus = csvRows.filter(_ => values(_).some(_ => _)).map(_ => trim(_.SKU));
      setItems((await serial(chunk(skus.map((_, i) => i), 10), _ => Promise.all(_.map(i => validate(skus, i))))).flat());
    } catch(e) {
      console.error(e);
      toast.error('CSVの読み込みに失敗しました');
    }
    toggleValidating(false);
    target.value = '';
  };
  const errorItems = (items || []).filter(_ => !isEmpty(_.errors));
  const isSubmittable = !isEmpty(items) && isEmpty(errorItems) && !isSubmitting;
  const onSubmit = async () => {
    toggleSubmitting(true);
    try {
      await batch(db, items, (batch, { product }) => batch.delete(product.ref), 400);
      toast.success(`${items.length}件の商品を削除しました`);
      onFinish && await onFinish();
      onClickClose();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
      toggleSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalHeader>CSV一括削除</ModalHeader>
      <ModalBody>
        <div className="small text-muted mb-2">
          SKU列のあるCSVを選択し、内容を確認して削除します。在庫がある商品、一度でも入出荷があった商品、ケース・セット商品の構成品は削除できません。
        </div>
        <Input type="file" accept=".csv" onChange={onSelectFile} disabled={isValidating || isSubmitting} className="mb-3" />
        {isValidating && <div className="text-muted"><span className="fas fa-spin fa-spinner mr-1" />検証中…</div>}
        {
          items != null && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>{fileName}（{items.length}行）</strong>
                <span className={isEmpty(errorItems) ? 'text-success' : 'text-danger'}>
                  {isEmpty(errorItems) ? `削除対象 ${items.length}件` : `${errorItems.length}件のエラー`}
                </span>
              </div>
              <div style={{ maxHeight: 480, overflowY: 'auto' }} className="border rounded">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>#</th>
                      <th style={{ minWidth: 100 }}>SKU</th>
                      <th style={{ minWidth: 80 }}>商品ID</th>
                      <th style={{ minWidth: 100 }}>商品名</th>
                      <th style={{ minWidth: 100 }}>状態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      items.map(({ i, sku, product, errors }) => {
                        const hasErrors = !isEmpty(errors);
                        return (
                          <tr key={i} className={hasErrors ? 'table-danger' : ''}>
                            <td>{i + 1}</td>
                            <td>{sku}</td>
                            <td>{product && idWithTenant(product.ref)}</td>
                            <td>{product?.name}</td>
                            <td className="small text-nowrap">
                              {
                                hasErrors ? (
                                  <span className="text-danger">{errors.map(_ => <div key={_}>{_}</div>)}</span>
                                ) : (
                                  <span className="text-danger">削除</span>
                                )
                              }
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
              {!isEmpty(errorItems) && <div className="text-danger small mt-2">エラーがあるため削除できません。</div>}
            </div>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClickClose} disabled={isSubmitting}>閉じる</Button>
        <Button color="danger" onClick={onSubmit} disabled={!isSubmittable}>
          {isSubmitting && <span className="fas fa-spin fa-spinner mr-1" />}
          {isEmpty(items) ? '削除' : `${items.length}件を削除`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
